// Phone numbers, checked with libphonenumber-js (Google's libphonenumber
// rules). The "max" metadata checks the digits against each country's real
// number ranges, not just the length, so "50 123 4567" passes for the UAE
// and "50 123" or "12 345 6789" do not.
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";
import examples from "libphonenumber-js/mobile/examples";

export const DEFAULT_COUNTRY = "AE";

// Where most applicants are from; shown first in the country list.
export const PREFERRED_COUNTRIES = ["AE", "IN", "PK", "PH", "LK", "NP", "BD", "EG", "GB"];

// A landline can't receive the WhatsApp or SMS the recruitment team sends.
const MOBILE_TYPES = new Set(["MOBILE", "FIXED_LINE_OR_MOBILE", undefined]);

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();

export const countryName = (iso) => {
  try {
    return regionNames?.of(iso) || iso;
  } catch {
    return iso;
  }
};

// Regional-indicator emoji ("AE" -> 🇦🇪). Windows shows the two letters instead.
export const flagOf = (iso) =>
  String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

export const COUNTRIES = getCountries()
  .map((iso) => ({ iso, name: countryName(iso), code: getCountryCallingCode(iso) }))
  .sort((a, b) => a.name.localeCompare(b.name));

const byIso = new Map(COUNTRIES.map((c) => [c.iso, c]));
export const countryOf = (iso) => byIso.get(iso) ?? byIso.get(DEFAULT_COUNTRY);

const normalise = (text) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

// Match on name ("emir"), ISO code ("ae") or calling code ("971", "+971").
// Best matches first: exact ISO or calling code, then names that start with
// the query, then names that merely contain it.
export function searchCountries(query) {
  const q = normalise(query || "");
  if (!q) return COUNTRIES;
  const code = q.replace(/^\+/, "");
  const numeric = /^\d+$/.test(code);
  const rank = (c) => {
    const name = normalise(c.name);
    if (c.iso.toLowerCase() === q || (numeric && c.code === code)) return 0;
    if (name.startsWith(q) || (numeric && c.code.startsWith(code))) return 1;
    if (name.includes(q)) return 2;
    return -1;
  };
  return COUNTRIES.map((c) => [rank(c), c])
    .filter(([r]) => r >= 0)
    .sort((a, b) => a[0] - b[0])
    .map(([, c]) => c);
}

export function examplePhone(iso) {
  try {
    return getExampleNumber(iso, examples)?.formatNational() ?? "";
  } catch {
    return "";
  }
}

// What the person typed, read as an international number when it starts
// with + or 00 (so pasting "+44 7911 123456" picks the UK by itself).
export function parseTyped(text, iso) {
  const trimmed = String(text || "").trim();
  const international = trimmed.replace(/^00/, "+");
  try {
    return international.startsWith("+")
      ? parsePhoneNumberFromString(international)
      : parsePhoneNumberFromString(trimmed, iso);
  } catch {
    return undefined;
  }
}

// null when the number is a valid mobile for the country, else the message
// to show. Expects the national part, as typed, plus the chosen country.
export function phoneProblem(text, iso) {
  const digits = String(text || "").replace(/\D/g, "");
  if (!digits) return "Enter your mobile number";
  const parsed = parseTyped(text, iso);
  const example = examplePhone(parsed?.country || iso);
  const like = example ? `, like ${example}` : "";
  if (!parsed?.isValid()) {
    return `Enter a valid ${countryName(parsed?.country || iso)} number${like}`;
  }
  if (!MOBILE_TYPES.has(parsed.getType())) {
    return "That looks like a landline. Enter a mobile number we can call or message.";
  }
  return null;
}

// "+971501234567" for a valid number, else null.
export function toE164(text, iso) {
  const parsed = parseTyped(text, iso);
  return parsed?.isValid() ? parsed.number : null;
}

// Pretty national format ("050 123 4567") once a number is valid.
export function formatNational(text, iso) {
  const parsed = parseTyped(text, iso);
  return parsed?.isValid() ? parsed.formatNational() : text;
}

// "+971 50 123 4567" for the review step.
export function formatInternational(text, iso) {
  const parsed = parseTyped(text, iso);
  return parsed?.isValid() ? parsed.formatInternational() : text;
}
